import { log } from '../../util/log';
import { BookingContext } from '../../util/booking-context';
import { callWp } from '../../util/call-wp';
import { hideLoadingOverlay, showLoadingOverlay } from '../../util/loading-overlay';
import { loadBookingConfirmedPage } from './booking-confirmed';
import { initCountdowns } from '../../util/countdown';
import { loadFindTimeslotPage } from './find-timeslot';
import { loadChooseServicesPage } from './choose-services';
import { track } from '../../util/tracking';

export async function loadConfirmAppointmentPage(context: BookingContext) {
	window.scroll(0, 0);
	log('Loading confirm appointment page');
	showLoadingOverlay(context);
	context.root.innerHTML = '';
	const ssrStartTime = performance.now();
	const shouldDepositBeRequired = context.data.categoryName === 'Cosmetic Injectables' && !context.data.reschedule;
	// Force deposit for testing
	// shouldDepositBeRequired = true;
	const ssrResponse = await callWp('GET', 'view_confirm_appointment', {
		...context.data,
		depositRequired: shouldDepositBeRequired,
	});
	const ssrEndTime = performance.now();
	log('SSR took', ssrEndTime - ssrStartTime, 'ms');
	const { html, depositRequired, depositUrl, trackingData: ssrTrackingData } = ssrResponse;
	context.root.innerHTML = html;
	hideLoadingOverlay(context);

	context.root
		.querySelectorAll<HTMLButtonElement>('button[data-reload]')
		.forEach((b) => b.addEventListener('click', () => loadConfirmAppointmentPage(context)));

	// TODO: Add event listeners

	const changeClinicButton = context.root.querySelector<HTMLButtonElement>('[data-change-clinic]');
	if (changeClinicButton) {
		// Remove the button if this is a reschedule
		if (context.data.reschedule) {
			changeClinicButton.remove();
		} else {
			changeClinicButton.addEventListener('click', showLocationModal);
		}
	}

	const changeDateButton = context.root.querySelector<HTMLButtonElement>('[data-change-time]');
	if (changeDateButton) {
		changeDateButton.addEventListener('click', async () => {
			showLoadingOverlay(context);
			await callWp('POST', 'cancel_slot', context.data);
			loadFindTimeslotPage(context);
		});
	}

	// data-change-treatments
	const changeTreatmentsButton = context.root.querySelector<HTMLButtonElement>('[data-change-treatments]');
	if (changeTreatmentsButton) {
		changeTreatmentsButton.addEventListener('click', async () => {
			// Remove the button if this is a reschedule
			if (context.data.reschedule) {
				changeTreatmentsButton.remove();
			} else {
				showLoadingOverlay(context);
				await callWp('POST', 'cancel_slot', context.data);
				loadChooseServicesPage(context);
			}
		});
	}

	const confirmResponsible = context.root.querySelector<HTMLInputElement>('#confirmResponsible');
	const confirmPrerequisites = context.root.querySelector<HTMLInputElement>('#confirmPrerequisites');

	if (!confirmResponsible || !confirmPrerequisites) {
		log('No confirmResponsible or confirmPrerequisites found');
		return;
	}

	confirmResponsible.addEventListener('change', () => {
		checkNext();
	});

	confirmPrerequisites.addEventListener('change', () => {
		checkNext();
	});

	const nextButton = context.root.querySelector<HTMLButtonElement>('[data-next]');
	if (!nextButton) {
		log('No next button found');
		return;
	}

	const checkNext = () => {
		nextButton.disabled = !(confirmResponsible.checked && confirmPrerequisites.checked);
	};

	if (!context.data.reschedule && !context.data.book) {
		context.root.querySelector('[data-step="1"]')?.addEventListener('click', async () => {
			showLoadingOverlay(context);
			await callWp('POST', 'cancel_slot', context.data);
			loadChooseServicesPage(context);
		});
	}
	context.root.querySelector('[data-step="2"]')?.addEventListener('click', async () => {
		showLoadingOverlay(context);
		await callWp('POST', 'cancel_slot', context.data);
		loadFindTimeslotPage(context);
	});

	const backButton = context.root.querySelector<HTMLButtonElement>('[data-back]');
	if (backButton) {
		backButton.addEventListener('click', async () => {
			showLoadingOverlay(context);
			await callWp('POST', 'cancel_slot', context.data);
			loadFindTimeslotPage(context);
		});
	}

	nextButton.addEventListener('click', async () => {
		showLoadingOverlay(context);

		if (depositRequired) {
			// Redirect to payment page
			window.location.href = depositUrl;
			return;
		}

		if (nextButton.dataset.depositRequired === 'true') {
			await loadDepositPage(context, nextButton.dataset.depositUrl ?? '');
			return;
		}

		const response = await callWp('POST', 'confirm_booking', context.data);

		if (response?.error || !response?.success) {
			if (!document.getElementById('unexpectedErrorModal')) {
				const modalHTML = `
                    <div class="modal fade" id="unexpectedErrorModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                            <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                            <span>An unexpected error occurred. Please try again later.</span>
                            </div>
                            <div class="d-flex flex-row gap-2">
                            <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">CONTINUE</button>
                            </div>
                        </div>
                        </div>
                    </div>
                    `;
				document.body.insertAdjacentHTML('beforeend', modalHTML);
			}

			const modalElement = document.getElementById('unexpectedErrorModal')!;

			const modal = new window.bootstrap.Modal(modalElement, {
				backdrop: 'static',
				keyboard: false,
			});

			// on next, select clinic
			modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
				modal.hide();
				window.location.href = `${silkNexusWp.myAccountUrl}/appointments`;
			});

			modal.show();
			return;
		}

		await loadBookingConfirmedPage(context);
	});

	initCountdowns(context, () => {
		if (!document.getElementById('reservationTimeExpiredModal')) {
			const modalHTML = `
                <div class="modal fade" id="reservationTimeExpiredModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
                    <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content" style="padding: 2rem; max-width: 28rem">
                        <div class="modal-body mb-3 p-0 d-flex flex-column align-items-center justify-content-center text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" height="2em" viewBox="0 0 512 512" style="margin-bottom: 1rem"><path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/></svg>
                        <span>Reservation time has expired. Please choose another time.</span>
                        </div>
                        <div class="d-flex flex-row gap-2">
                        <button type="button" class="w-100 btn btn-primary text-white" style="font-weight: 700; font-size: 0.875rem">CONTINUE</button>
                        </div>
                    </div>
                    </div>
                </div>
                `;
			document.body.insertAdjacentHTML('beforeend', modalHTML);
		}

		const modalElement = document.getElementById('reservationTimeExpiredModal')!;

		const modal = new window.bootstrap.Modal(modalElement, {
			backdrop: 'static',
			keyboard: false,
		});

		// on next, select clinic
		modalElement.querySelector<HTMLButtonElement>('.btn-primary')!.addEventListener('click', () => {
			modal.hide();
			context.data.slotTime = null;
			loadFindTimeslotPage(context);
		});

		modal.show();
	});

	track({
		event: 'pageview',
		page_location: '/booking-online/booking-summary/',
		page_title: 'Booking online - 4 - Booking Summary',
	});
	track({
		event: 'bookingSummary',
		...(ssrTrackingData ?? {}),
	});
}

async function loadDepositPage(context: BookingContext, depositUrl: string) {
	window.location.href = depositUrl;
}
